import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Envelope } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CopyPassword } from "@/components/user-wizard/WizardFinish";

export const INVITE = ["invited", "invitation_pending", "invitation_expired"];
const BTN = "border border-black px-3 py-1.5 text-xs uppercase tracking-widest transition-colors duration-100 disabled:opacity-50";
const errText = (e, fallback) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fallback);

// Temporary-password accounts that have never signed in.
export const needsWelcome = (u) => u.status === "active" && !!u.must_change_password && !u.last_login_at && u.role !== "super_admin";

// Invites get a fresh link; temporary-password accounts a fresh password. Returns the password only if the email failed.
async function resendFor(u) {
  if (INVITE.includes(u.status)) {
    const { data } = await api.post(`/employees/${u.id}/resend-invitation`);
    navigator.clipboard?.writeText(data.invitation_url).catch(() => {});
    if (data.email_sent) {
      toast.success(`Invitation resent to ${u.email}`);
    } else {
      toast.warning("The invitation email couldn't be sent. The link is copied so you can share it.");
    }
    return null;
  }
  const { data } = await api.post(`/_admin/user-wizard/accounts/${u.id}/resend`);
  if (data.email_sent) {
    toast.success(`Welcome email sent to ${u.email}`);
  }
  return data.email_sent ? null : data.temp_password;
}

export function PasswordFallback({ pw, email, onClose }) {
  return (
    <Dialog open={!!pw} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none border-2 border-black sm:max-w-md" data-testid="resend-welcome-fallback">
        <DialogHeader>
          <DialogTitle className="font-chivo text-xl font-black">The email couldn&apos;t be sent</DialogTitle>
          <DialogDescription>Share these sign-in details with them another way. Username: {email}</DialogDescription>
        </DialogHeader>
        <CopyPassword pw={pw} />
      </DialogContent>
    </Dialog>
  );
}

export function ResendWelcomeButton({ user, testId, onDone, className }) {
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState(null);
  const send = async () => {
    setBusy(true);
    try {
      setPw(await resendFor(user));
      onDone?.();
    } catch (e) {
      toast.error(errText(e, "Couldn't resend"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button type="button" disabled={busy} onClick={send} data-testid={testId} className={className}>
        <Envelope size={10} className="inline" /> {busy ? "Sending…" : "Resend welcome"}
      </button>
      <PasswordFallback pw={pw} email={user.email} onClose={() => setPw(null)} />
    </>
  );
}

// /employees?resend=<id> (the link in the "hasn't used their account" email) opens a confirm for that person.
export function ResendFromLink({ rows, onDone }) {
  const [params, setParams] = useSearchParams();
  const id = params.get("resend");
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState({ value: null, email: "" });
  useEffect(() => {
    if (!id || !rows.length) {
      return;
    }
    const row = rows.find((r) => r.id === id);
    if (row) {
      setTarget(row);
    } else {
      toast.error("That person isn't in this list. Clear any filters and try the link again.");
      params.delete("resend");
      setParams(params, { replace: true });
    }
  }, [id, rows]); // eslint-disable-line react-hooks/exhaustive-deps
  const close = () => {
    setTarget(null);
    params.delete("resend");
    setParams(params, { replace: true });
  };
  const invite = !!target && INVITE.includes(target.status);
  const pending = !!target && (invite || needsWelcome(target));
  const confirm = async () => {
    setBusy(true);
    try {
      setPw({ value: await resendFor(target), email: target.email });
      close();
      onDone?.();
    } catch (e) {
      toast.error(errText(e, "Couldn't resend"));
    } finally {
      setBusy(false);
    }
  };
  const desc = !pending ? "They've already signed in, so there's nothing to resend."
    : INVITE.includes(target.status) ? "They'll get a fresh invitation link." : "They'll get a fresh temporary password and the getting-started steps.";
  return (
    <>
      <Dialog open={!!target} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="rounded-none border-2 border-black sm:max-w-md" data-testid="resend-from-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo text-xl font-black">{pending ? `Resend ${target?.name}'s ${invite ? "invitation" : "welcome email"}?` : `${target?.name} is all set`}</DialogTitle>
            <DialogDescription>{target?.email} — {desc}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button type="button" onClick={close} className={`${BTN} hover:bg-zinc-100`} data-testid="resend-from-link-cancel">{pending ? "Cancel" : "Close"}</button>
            {pending && (
              <button type="button" disabled={busy} onClick={confirm} className={`${BTN} bg-black text-white hover:bg-zinc-800`} data-testid="resend-from-link-confirm">
                {busy ? "Sending…" : "Resend"}
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PasswordFallback pw={pw.value} email={pw.email} onClose={() => setPw({ value: null, email: "" })} />
    </>
  );
}
